import { useEffect, type ReactNode } from 'react';

import { Stack } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './ViewsContainer.module.scss';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';
import { useHorizontalOverflow } from '@/hooks/ui';

interface ViewsContainerProps {
  children: ReactNode;
  actions?: ReactNode;
  currentViewId?: string | null;
  hideScrollButtonsOnNoOverflow?: boolean;
}

export const ViewsContainer = ({
  children,
  actions,
  currentViewId,
  hideScrollButtonsOnNoOverflow = false,
}: ViewsContainerProps) => {
  const { t } = useTranslation('views');
  const {
    scrollerRef,
    hasOverflowLeft,
    hasOverflowRight,
    scrollLeft,
    scrollRight,
  } = useHorizontalOverflow();

  const hasOverflow = hasOverflowLeft || hasOverflowRight;

  // Keyboard navigation — scroll focused view items into view, skip on pointer clicks
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let lastPointerDownTime = 0;

    const handlePointerDown = () => {
      lastPointerDownTime = performance.now();
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (performance.now() - lastPointerDownTime < 300) {
        return;
      }
      const target = e.target as HTMLElement;
      const viewItem = target.closest('[data-view-item]');
      if (viewItem) {
        viewItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    };

    scroller.addEventListener('pointerdown', handlePointerDown);
    scroller.addEventListener('focusin', handleFocusIn);
    return () => {
      scroller.removeEventListener('pointerdown', handlePointerDown);
      scroller.removeEventListener('focusin', handleFocusIn);
    };
  }, [scrollerRef]);

  // Scroll the active view into view when currentViewId changes
  useEffect(() => {
    if (!currentViewId) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const activeView = scroller.querySelector('[data-view-link-active]');
    if (activeView) {
      requestAnimationFrame(() => {
        activeView.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      });
    }
  }, [currentViewId, scrollerRef]);

  return (
    <div
      className={cx(styles.ViewsContainer, {
        [styles.HasScrollButtons]:
          hasOverflow || !hideScrollButtonsOnNoOverflow,
        [styles.HasActions]: !!actions,
      })}
    >
      <div className={styles.ViewsScrollContainer}>
        {hasOverflowLeft && <div className={styles.GradientLeft} />}

        <Stack className={styles.Buttons} direction="row" gap={0.5}>
          {(!hideScrollButtonsOnNoOverflow || hasOverflow) && (
            <>
              <IconButton
                size="tiny"
                iconName={uiIcons.navigation.back}
                tooltip={t('ScrollLeft')}
                onClick={scrollLeft}
                aria-label={t('ScrollLeft')}
                disabled={!hasOverflowLeft}
                data-automation-id="scroll-left-button"
              />
              <IconButton
                size="tiny"
                iconName={uiIcons.navigation.next}
                tooltip={t('ScrollRight')}
                onClick={scrollRight}
                aria-label={t('ScrollRight')}
                disabled={!hasOverflowRight}
                data-automation-id="scroll-right-button"
              />
            </>
          )}
          {actions}
        </Stack>

        <div ref={scrollerRef} className={styles.ViewsScroller}>
          <div className={styles.Views}>{children}</div>
        </div>

        {hasOverflowRight && <div className={styles.GradientRight} />}
      </div>
    </div>
  );
};
